import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SKILLS_CATALOG, type Skill } from "@/lib/skills";
import { Check, Plus, Search, X } from "lucide-react";

interface SkillSelectorProps {
  selectedSkills: string[];
  onChange: (skills: string[]) => void;
  maxSkills?: number;
  label?: string;
}

const CATEGORY_LABELS: Record<Skill['category'], string> = {
  tecnica: "Técnicas",
  comportamental: "Comportamentais",
  idioma: "Idiomas",
};

export function SkillSelector({ selectedSkills, onChange, maxSkills = 12, label }: SkillSelectorProps) {
  const [search, setSearch] = useState("");

  const filteredSkills = SKILLS_CATALOG.filter(s =>
    s.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    if (selectedSkills.includes(id)) {
      onChange(selectedSkills.filter(s => s !== id));
    } else if (selectedSkills.length < maxSkills) {
      onChange([...selectedSkills, id]);
    }
  };

  const grouped = (['tecnica', 'comportamental', 'idioma'] as const).map(cat => ({
    category: cat,
    label: CATEGORY_LABELS[cat],
    skills: filteredSkills.filter(s => s.category === cat),
  })).filter(g => g.skills.length > 0);

  return (
    <div className="space-y-4">
      {label && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">{selectedSkills.length}/{maxSkills}</span>
        </div>
      )}

      {/* Selected */}
      {selectedSkills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedSkills.map(id => {
            const skill = SKILLS_CATALOG.find(s => s.id === id);
            if (!skill) return null;
            return (
              <Badge
                key={id}
                variant="default"
                className="cursor-pointer gap-1"
                onClick={() => toggle(id)}
              >
                {skill.label}
                <X className="w-3 h-3" />
              </Badge>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar habilidade..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Categories */}
      <div className="space-y-3 max-h-60 overflow-y-auto">
        {grouped.map(group => (
          <div key={group.category}>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">{group.label}</p>
            <div className="flex flex-wrap gap-1.5">
              {group.skills.map(skill => {
                const isSelected = selectedSkills.includes(skill.id);
                return (
                  <Badge
                    key={skill.id}
                    variant={isSelected ? "default" : "outline"}
                    className="cursor-pointer gap-1"
                    onClick={() => toggle(skill.id)}
                  >
                    {isSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {skill.label}
                  </Badge>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
